import './LocalPage.css';
import React from 'react';
import { Button, Alert, ProgressBar, Carousel } from 'react-bootstrap';
import { connect } from 'react-redux';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import * as sagas from '../../store/sagas';
import RTViewer from '../rtviewer/RTViewer';
import { StoreState } from '../../store/store';
import { ErrorBoundary } from '../ErrorBoundary';

import { DISPLAY_VERSION, NOT_FOR_CLINICAL_USE_LABELING, SECURITY_DISCLAIMER, getAppName } from '../../environments';
import GlobalToolbar from '../common/GlobalToolbar';
import { ServiceAgreementSummary, PseudonymizedTagsSummary } from '../common/ServiceAgreement';
import { sleep } from '../../util';
import LandingPageLayout from '../LandingPageLayout';

type OwnProps = {}
type DispatchProps = {
    unloadScan(scanId: string): void,
    unloadStructureSet(scanId: string, structureSetId: string): void,
    storeLocalScan(files: File[], cbReturnId: (scanId: string) => Promise<void>, allowMissingMetaheaderFix?: boolean): void,
    storeLocalStructureSet(file: File, currentScanId: string, cbReturnId: (scanId: string, ssId: string) => void): void,
    clearFileLoadErrors(): void,
}

type AllProps = OwnProps & StoreState & DispatchProps;
type OwnState = {
    showRTViewer: boolean,
    immediatelyOpenScan: boolean,
    scanId?: string | null,
    structureSetId?: string | null,
    slideIndex: number;
}

const PREVENT_OPEN_IF_ERRORS = true;

type CarouselItem = { url: string, captionHeader?: string, captionText?: string };

const carouselItems: CarouselItem[] = [
    { url: '/img/carousel01.png', captionHeader: 'An image caption can go here', captionText: 'A description of the image can be put here, but it\'s optional' },
    { url: '/img/carousel02.png' }
];


export class LocalPage extends React.Component<AllProps, OwnState> {
    displayName = LocalPage.name

    constructor(props: AllProps) {
        super(props);

        this.state = { showRTViewer: false, immediatelyOpenScan: true, slideIndex: 0 };
    }

    openScan = async (scanId: string) => {
        // sleep for a bit to let progress bars etc finish
        await sleep(500);

        this.setState({ scanId: scanId });
        if (this.state.immediatelyOpenScan && (!this.props.fileLoadErrors || this.props.fileLoadErrors.length === 0)) {
            this.setState({ showRTViewer: true });
        }
    }

    openStructureSet = (scanId: string, structureSetId: string) => {
        if (this.state.scanId !== scanId) {
            alert("Structure set doesn't belong to current image");
        }
        else {
            this.setState({ structureSetId: structureSetId });
        }
    }

    handleImageFileChange = (e: any) => {
        this.props.clearFileLoadErrors();
        const allowMissingMetaheaderFix = this.props.applicationPermissions && this.props.applicationPermissions.allowMissingMetaheaderFix;
        if (this.state.scanId) {
            this.props.unloadScan(this.state.scanId);
        }
        e.persist();
        this.setState({ scanId: null, structureSetId: null }, () => {
            let files = [];
            for (let i = 0; i < e.target.files.length; i++) {
                files.push(e.target.files[i])
            }
            if (files.length > 0) {
                this.props.storeLocalScan(files, this.openScan, allowMissingMetaheaderFix);
            }
        });
    }

    handleStructureSetFileChange = (e: any) => {
        if (!this.state.scanId) return;
        let scanId: string = this.state.scanId;

        if (this.state.structureSetId) {
            this.props.unloadStructureSet(scanId, this.state.structureSetId);
        }
        e.persist();
        this.setState({ structureSetId: null }, () => {
            this.props.storeLocalStructureSet(e.target.files[0], scanId, this.openStructureSet);
        })
    }


    handleShowRTViewerClick = () => {
        this.setState({ showRTViewer: true });
    }

    handleBack = () => {
        this.setState({ showRTViewer: false });
    }

    handleImmediatelyOpenChange = () => {
        this.setState({ immediatelyOpenScan: !this.state.immediatelyOpenScan });
    }

    handleChangeSlide = (nextSlideIndex: number) => {
        this.setState({ slideIndex: nextSlideIndex });
    }

    renderFileForm = (isVisible: boolean) => {

        const { fileLoadErrors, localFileProgress, deploymentConfigInfo } = this.props;
        const isServiceAgreementVisible = this.props.applicationPermissions && this.props.applicationPermissions.showServiceAgreement;

        const isOpenPrevented = !PREVENT_OPEN_IF_ERRORS || (fileLoadErrors !== undefined && fileLoadErrors.length > 0);

        // by hiding instead of not rendering the file form we're able to save the state of the browser's built-in
        // file load forms, allowing us to show the number and names of files loaded in the UI after user presses
        // back on the main rtviewer view
        const hiddenStyle = isVisible ? '' : 'hide-file-form';

        const currentSlide = carouselItems[this.state.slideIndex];

        return (
            <LandingPageLayout
                className={hiddenStyle}
                toolbar={(
                    <div className="global-toolbar-for-local">
                        <GlobalToolbar isEmbedded={true} />
                    </div>
                )}
            >
                <p><b>{getAppName()}</b> can be used to auto-generate structure sets for input DICOM images.</p>

                <p>Images are pseudonymized before sending them to the delineation service.</p>

                <div className="image-form">
                    <p >1. Select DICOM image files (CT/MR):</p>
                    <div> <label><input name="OpenFileSelection" type="checkbox"
                        checked={this.state.immediatelyOpenScan} onChange={this.handleImmediatelyOpenChange} /> Immediately Open Selected Scan</label></div> <br />
                    <input type="file" ref="imageInput" onChange={this.handleImageFileChange} multiple />

                    {localFileProgress && (<div className="scan-progress-bar"><ProgressBar variant="info" now={localFileProgress.progress} max={localFileProgress.total} /></div>)}

                </div>
                {this.state.scanId ? <div className="rtstruct-form"><p>2. Select DICOM structure set files (optional):</p>
                    <input type="file" ref="structureSetInput" onChange={this.handleStructureSetFileChange} /></div> : null
                }
                {this.state.scanId && !isOpenPrevented ?
                    <Button variant="info" className="open-rtviewer-button btn btn-default  " onClick={this.handleShowRTViewerClick}>Open scan</Button>
                    : null
                }
                <br />

                {fileLoadErrors && fileLoadErrors.length > 0 && (
                    <Alert variant="danger" className="file-load-errors-alert">
                        <Alert.Heading>Errors occurred when trying to load following files:</Alert.Heading>
                        {fileLoadErrors.map(e => (
                            <div key={e.fileName} className="file-load-errors">
                                <div className="filename"><b>{e.fileName}</b></div>
                                <div className="error-message">{e.errorMessage}</div>
                            </div>
                        ))}
                    </Alert>
                )}

                <p>{SECURITY_DISCLAIMER}</p>

                {deploymentConfigInfo && deploymentConfigInfo.supportContactInformation && (
                    <div className="support-contact"><ReactMarkdown remarkPlugins={[remarkGfm]} children={deploymentConfigInfo.supportContactInformation} className="help-dialog-markdown" /></div>
                )}

                {isServiceAgreementVisible ? <ServiceAgreementSummary /> : <PseudonymizedTagsSummary /> }

                <div className="version-number">
                    <div>Version: {DISPLAY_VERSION}</div>
                    {deploymentConfigInfo && deploymentConfigInfo.regionInfo && (<div>Region: {deploymentConfigInfo.regionInfo}</div>)}
                    {deploymentConfigInfo && deploymentConfigInfo.regionLicenseUrl && (<div>By using this software you accept <a href={deploymentConfigInfo.regionLicenseUrl}>the terms of use.</a></div>)}
                </div>
            </LandingPageLayout>
        );
    }

    render() {
        const scanId = this.state.scanId;
        const loginFailed = this.props.loginError !== null;

        if (loginFailed) { return null; }

        return (
            <>
                {this.renderFileForm(!(this.state.showRTViewer && scanId))}
                {this.state.showRTViewer && scanId &&
                    (<ErrorBoundary handleError={this.handleBack}>
                        <RTViewer scanId={scanId} canEdit={false} canCreateRtstruct={true} hideSaveButtons={true} handleBack={this.handleBack} />
                    </ErrorBoundary>)}
            </>
        );
    }
}

export default connect(
    state => Object.assign({}, state),
    sagas.mapDispatchToProps
)(LocalPage);
